import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import PlanoAcaoModal from "./PlanoAcaoModal";
import AplicarAvaliacaoModal from "./AplicarAvaliacaoModal";
import GerarLinkAvaliacaoModal from "./GerarLinkAvaliacaoModal";
import ConsolidadoAvaliacoesModal from "./ConsolidadoAvaliacoesModal";
import AvaliacoesColaboradorModal from "./AvaliacoesColaboradorModal";
import { Avatar } from "./EndomarketingPecas";
import { norm } from "../endomarketing-utils";
import type { Row } from "../types";
import { ordenarPorNome, ordenarTextos } from "../ordem";
import { IconSearch, IconPlus, IconStar, IconCheck, IconSparkles, IconLink, IconEye } from "../icons";

/**
 * AVALIAÇÃO DE DESEMPENHO — a nota vira PLANO DE AÇÃO.
 *
 * A aba "Avaliações" registra a nota; esta responde "e agora?". Cada
 * colaborador ativo aparece com o que a empresa sabe do desempenho dele
 * (quantas avaliações, média, última nota) e com o plano de ação: quantas ações
 * existem, quantas foram concluídas e quantas estão atrasadas.
 *
 * Daqui se MONTA o plano (é onde a criação é habilitada): "Montar plano de
 * ação" para quem não tem, "Abrir plano" para acompanhar, e a IA sugere as
 * ações a partir das avaliações já registradas.
 */

type Filtro = "todos" | "com-plano" | "sem-plano" | "atrasados" | "sem-avaliacao";

const FILTROS: { key: Filtro; label: string }[] = [
  { key: "todos", label: "Todos" },
  { key: "com-plano", label: "Com plano" },
  { key: "sem-plano", label: "Sem plano" },
  { key: "atrasados", label: "Com ação atrasada" },
  { key: "sem-avaliacao", label: "Sem avaliação" },
];

export default function AvaliacaoDesempenhoView() {
  const [linhas, setLinhas] = useState<Row[]>([]);
  const [resumo, setResumo] = useState<Row>({});
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [busca, setBusca] = useState("");
  const [setor, setSetor] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [aviso, setAviso] = useState<string | null>(null);
  /** Plano aberto: id existente, ou colaborador para criar um novo. */
  const [aberto, setAberto] = useState<{ planoId: number | null; colaborador: Row } | null>(null);
  /** Formulário FO 094 aberto para um colaborador (ou para escolher na hora). */
  const [avaliar, setAvaliar] = useState<{ colaborador: Row | null } | null>(null);
  const [gerarLink, setGerarLink] = useState(false);
  const [consolidado, setConsolidado] = useState(false);
  /** Histórico de avaliações aberto pelo olho (todas as já realizadas). */
  const [historico, setHistorico] = useState<Row | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const r = await api.desempenho.listar();
      setLinhas(r.colaboradores ?? []);
      setResumo(r.resumo ?? {});
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), 6000);
    return () => clearTimeout(t);
  }, [aviso]);

  const setores = useMemo(
    () => ordenarTextos([...new Set(linhas.map((c) => c.departamento).filter(Boolean))] as string[]),
    [linhas],
  );

  const lista = useMemo(() => {
    const q = norm(busca);
    // Cards de colaborador em ordem alfabética (src/ordem.ts).
    return ordenarPorNome(linhas, (c) => c.nome).filter((c) => {
      if (setor && c.departamento !== setor) return false;
      if (q && !norm(c.nome).includes(q) && !norm(c.cargo).includes(q)) return false;
      if (filtro === "com-plano") return !!c.plano_id;
      if (filtro === "sem-plano") return !c.plano_id;
      if (filtro === "atrasados") return Number(c.plano_atrasadas) > 0;
      if (filtro === "sem-avaliacao") return Number(c.avaliacoes) === 0;
      return true;
    });
  }, [linhas, busca, setor, filtro]);

  const comPlano = linhas.filter((c) => c.plano_id).length;

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />;

  return (
    <div className="crud desemp">
      <div className="desemp-indicadores">
        <Indicador
          rotulo="Média das avaliações"
          valor={resumo.media_geral ? Number(resumo.media_geral).toFixed(1) : "—"}
          detalhe="de 0 a 10, todas as avaliações"
        />
        <Indicador
          rotulo="Planos em andamento"
          valor={resumo.planos_andamento ?? 0}
          detalhe={`${comPlano} de ${linhas.length} colaboradores com plano`}
        />
        <Indicador
          rotulo="Ações atrasadas"
          valor={resumo.acoes_atrasadas ?? 0}
          detalhe="prazo vencido e não concluídas"
          alerta={Number(resumo.acoes_atrasadas) > 0}
        />
        <Indicador
          rotulo="Vencem em 7 dias"
          valor={resumo.acoes_semana ?? 0}
          detalhe="ações com prazo na semana"
        />
        <Indicador
          rotulo="Planos concluídos"
          valor={resumo.planos_concluidos ?? 0}
          detalhe="todas as ações fechadas"
        />
      </div>

      {aviso && <div className="endo-aviso"><span>{aviso}</span></div>}

      <div className="crud-toolbar endo-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar colaborador ou cargo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <label className="endo-mes">
          <span className="muted">Setor</span>
          <select value={setor} onChange={(e) => setSetor(e.target.value)}>
            <option value="">Todos</option>
            {setores.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <div className="toolbar-right">
          <span className="badge">{lista.length} colaborador(es)</span>
          <button className="btn-secondary" onClick={() => setConsolidado(true)}>
            <IconStar size={17} /> Consolidado
          </button>
          <button className="btn-secondary" onClick={() => setGerarLink(true)}>
            <IconLink size={17} /> Gerar link p/ gestor
          </button>
          <button className="btn-primary" onClick={() => setAvaliar({ colaborador: null })}>
            <IconStar size={17} /> Avaliar (FO 094)
          </button>
        </div>
      </div>

      <div className="endo-chips">
        {FILTROS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`endo-chip ${filtro === f.key ? "ativo" : ""}`}
            onClick={() => setFiltro(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="empty">Carregando…</div>
      ) : lista.length === 0 ? (
        <div className="empty">Nenhum colaborador com esses filtros.</div>
      ) : (
        <div className="desemp-lista">
          {lista.map((c) => (
            <LinhaColaborador
              key={c.id}
              c={c}
              onAbrir={() => setAberto({ planoId: Number(c.plano_id), colaborador: c })}
              onNovo={() => setAberto({ planoId: null, colaborador: c })}
              onAvaliar={() => setAvaliar({ colaborador: c })}
              onVerAvaliacoes={() => setHistorico(c)}
            />
          ))}
        </div>
      )}

      {avaliar && (
        <AplicarAvaliacaoModal
          colaboradores={linhas}
          colaboradorInicial={avaliar.colaborador ? Number(avaliar.colaborador.id) : null}
          onClose={() => setAvaliar(null)}
          onSalvo={async (msg) => {
            setAvaliar(null);
            setAviso(msg);
            await carregar();
          }}
        />
      )}
      {gerarLink && (
        <GerarLinkAvaliacaoModal colaboradores={linhas} onClose={() => setGerarLink(false)} />
      )}
      {consolidado && <ConsolidadoAvaliacoesModal onClose={() => setConsolidado(false)} />}
      {historico && (
        <AvaliacoesColaboradorModal colaborador={historico} onClose={() => setHistorico(null)} />
      )}

      {aberto && (
        <PlanoAcaoModal
          planoId={aberto.planoId}
          colaborador={aberto.colaborador}
          onClose={() => setAberto(null)}
          onSalvo={async (msg) => {
            setAberto(null);
            setAviso(msg);
            await carregar();
          }}
        />
      )}
    </div>
  );
}

function Indicador({
  rotulo,
  valor,
  detalhe,
  alerta,
}: {
  rotulo: string;
  valor: number | string;
  detalhe: string;
  alerta?: boolean;
}) {
  return (
    <div className={`endo-ind desemp-ind ${alerta ? "alerta" : ""}`}>
      <span className="endo-ind-valor">{valor}</span>
      <span className="endo-ind-rotulo">{rotulo}</span>
      <span className="endo-ind-detalhe">{detalhe}</span>
    </div>
  );
}

function LinhaColaborador({
  c,
  onAbrir,
  onNovo,
  onAvaliar,
  onVerAvaliacoes,
}: {
  c: Row;
  onAbrir: () => void;
  onNovo: () => void;
  onAvaliar: () => void;
  onVerAvaliacoes: () => void;
}) {
  const acoes = Number(c.plano_acoes ?? 0);
  const feitas = Number(c.plano_concluidas ?? 0);
  const atrasadas = Number(c.plano_atrasadas ?? 0);
  const pct = acoes > 0 ? Math.round((feitas / acoes) * 100) : 0;
  const nota = c.ultima_nota == null ? null : Number(c.ultima_nota);

  return (
    <article className={`desemp-card ${atrasadas > 0 ? "atrasado" : ""}`}>
      <Avatar pessoa={{ nome: String(c.nome), foto: c.foto }} tamanho="m" />

      <div className="desemp-pessoa">
        <strong>{c.nome}</strong>
        <small className="muted">{c.cargo || c.departamento || "—"}</small>
      </div>

      <div className="desemp-nota">
        {nota == null ? (
          <span className="muted">sem avaliação</span>
        ) : (
          <>
            <span className={`desemp-badge-nota ${nota >= 8 ? "boa" : nota >= 6 ? "media" : "baixa"}`}>
              <IconStar size={13} /> {nota.toFixed(1)}
            </span>
            <small className="muted">
              {c.ultimo_periodo ? `${c.ultimo_periodo} · ` : ""}
              {c.avaliacoes} avaliação(ões) · média {c.media ?? "—"}
            </small>
          </>
        )}
      </div>

      <div className="desemp-plano">
        {c.plano_id ? (
          <>
            <div className="desemp-plano-topo">
              <span className={`desemp-situacao ${norm(c.plano_situacao).replace(/\s+/g, "-")}`}>
                {c.plano_situacao}
              </span>
              <small className="muted">
                {feitas} de {acoes} ação(ões)
                {atrasadas > 0 && <b className="desemp-atraso"> · {atrasadas} atrasada(s)</b>}
              </small>
            </div>
            <div className="endo-barra">
              <div className="endo-barra-fill" style={{ width: `${pct}%` }} />
            </div>
          </>
        ) : (
          <span className="muted desemp-sem-plano">Ainda sem plano de ação</span>
        )}
      </div>

      <div className="desemp-acoes">
        {/*
          O olho abre as avaliações JÁ FEITAS (questão a questão). Só aparece
          para quem tem alguma — botão que abre lista vazia só gera dúvida.
        */}
        {Number(c.avaliacoes ?? 0) > 0 && (
          <button
            className="btn-secondary btn-sm"
            onClick={onVerAvaliacoes}
            title="Ver as avaliações já realizadas deste colaborador"
          >
            <IconEye size={14} /> Ver avaliações
          </button>
        )}
        {/* Avaliar vem depois: é a nota que dá origem ao plano. */}
        <button className="btn-secondary btn-sm" onClick={onAvaliar} title="Preencher o formulário FO 094">
          <IconStar size={14} /> Avaliar
        </button>
        {c.plano_id ? (
          <>
            <button className="btn-secondary btn-sm" onClick={onAbrir}>
              {feitas === acoes && acoes > 0 ? <IconCheck size={14} /> : null} Abrir plano
            </button>
            <button className="btn-secondary btn-sm" onClick={onNovo} title="Criar um plano novo (novo ciclo)">
              <IconPlus size={14} /> Novo
            </button>
          </>
        ) : (
          <button className="btn-primary btn-sm" onClick={onNovo}>
            <IconSparkles size={14} /> Montar plano de ação
          </button>
        )}
      </div>
    </article>
  );
}
